import { useEffect, useState } from "react"
import { useAtom } from "jotai"
import { userAtom, isSuperAdminAtom, isGuideAdminAtom, isOrgAdminAtom } from "../../state"
import AdminLayout from "../../Components/Layout/AdminLayout"
import StatsCard from "../../Components/Admin/StatsCard"
import QuickActions from "../../Components/Admin/QuickActions"
import ActivityFeed from "../../Components/Admin/ActivityFeed"
import api from "../../services/api"
import { cn } from "../../lib/utils"

const AdminDashboard = () => {
  const [user] = useAtom(userAtom)
  const [isSuperAdmin] = useAtom(isSuperAdminAtom)
  const [isOrgAdmin] = useAtom(isOrgAdminAtom)
  const [stats, setStats] = useState(null)
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.getAdminStats(),
      api.getAdminActivity()
    ])
      .then(([statsResponse, activityResponse]) => {
        setStats(statsResponse.data)
        setActivity(activityResponse.data)
        setLoading(false)
      })
      .catch(error => {
        console.error("Failed to load dashboard data:", error)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center text-gray-600">Loading...</div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl p-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.first_name || user?.name}
          </h1>
          <div className="mt-2">
            {isSuperAdmin ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-brandSecondary px-3 py-1 text-xs font-semibold text-white">
                <i className="fa fa-crown"></i>
                Super Admin
              </span>
            ) : isOrgAdmin ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">
                <i className="fa fa-user-shield"></i>
                Organization Admin
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-circle px-3 py-1 text-xs font-semibold text-white">
                <i className="fa fa-compass"></i>
                Guide
              </span>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div
          className={cn(
            "mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3",
            isSuperAdmin && "lg:grid-cols-5",
            isOrgAdmin && "lg:grid-cols-4",
          )}
        >
          <StatsCard icon="fa-clipboard-list" label="Total Plans" value={stats?.total_plans || 0} />
          <StatsCard
            icon="fa-chart-line"
            label="Active Plans"
            value={stats?.active_plans || 0}
            borderColor="border-brandSecondary"
          />
          <StatsCard
            icon="fa-check-circle"
            label="Completed Plans"
            value={stats?.completed_plans || 0}
            borderColor="border-gifts"
          />
          {/* Super Admin Only: Organizations Stat */}
          {isSuperAdmin ? (
            <StatsCard
              icon="fa-building"
              label="Organizations"
              value={stats?.total_organizations || 0}
              borderColor="border-vision"
            />
          ) : null}
          {isOrgAdmin ? (
            <StatsCard
              icon="fa-users"
              label="Total Users"
              value={stats?.total_users || 0}
              borderColor="border-circle"
            />
          ) : null}
        </div>

        {/* Quick Actions */}
        <QuickActions />

        {/* Recent Activity */}
        <div className="mt-8">
          <ActivityFeed activities={activity} />
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminDashboard
